import React from "react";
import { Control, Controller, FieldPath, FieldValues } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useStoreState } from "ezStore";

import { FormGroup } from "@cofano/cck";
import { Table, TableBody, TableCell, TableContainer, TableRow } from "@mui/material";

import { FormErrorMessage } from "components/form/FormErrorMessage";
import DatePicker from "components/cck/DatePicker";
import { Fields, hasOptionalFieldPermission, usePermission } from "components/utils/Permission";

import { VoyageCallSchemaInput } from "view/voyages/utils/voyage.schema";

type VoyageCallFieldName = keyof VoyageCallSchemaInput;

interface VoyageCallTimesFormFieldsProps<TFieldValues extends FieldValues = FieldValues,> {
	control: Control<TFieldValues>;
	index?: number;
}

const VoyageCallTimesFormFields = <TFieldValues extends FieldValues,>({
	control,
	index,
}: VoyageCallTimesFormFieldsProps<TFieldValues>) => {

	const session = useStoreState(store => store.session);
	const { isRTR } = usePermission();

	const hasNotificationTimes = hasOptionalFieldPermission(session, Fields.CALL, "notificationTime");

	const getFieldName = (fieldName: VoyageCallFieldName): FieldPath<TFieldValues> => {
		if (index !== undefined) {
			return `calls.${index}.${fieldName}` as FieldPath<TFieldValues>;
		}
		return fieldName as FieldPath<TFieldValues>;
	};

	const ptaName = getFieldName("pta");
	const ptdName = getFieldName("ptd");
	const ataName = getFieldName("ata");
	const etaName = getFieldName("eta");
	const dtnName = getFieldName("dtn");
	const atnName = getFieldName("atn");
	const etsoName = getFieldName("etso");
	const atsoName = getFieldName("atso");
	const eteoName = getFieldName("eteo");
	const ateoName = getFieldName("ateo");
	const atdName = getFieldName("atd");
	const etdName = getFieldName("etd");

	return (
		<TableContainer >
			<Table sx={{
				"td, th": {
					padding: "4px 6px",
					verticalAlign: "bottom",
				},
				"& td, & th": { border: 0, verticalAlign: "bottom" },
			}}>
				<TableBody>
					<TableRow>
						<TableCell style={{ minWidth: 150 }}></TableCell>

						<TableCell>
							<FormGroup label={intl.formatMessage({ id: "PLANNED" })} required />
						</TableCell>

						<TableCell>
							<FormGroup label={intl.formatMessage({ id: "ESTIMATED" })} />
						</TableCell>

						<TableCell>
							<FormGroup label={intl.formatMessage({ id: "ACTUAL" })} />
						</TableCell>

						{hasNotificationTimes && (
							<TableCell>
								<FormGroup label={intl.formatMessage({ id: "NOTIFIED" })} />
							</TableCell>
						)}
					</TableRow>

					<TableRow>
						<TableCell>
							<FormGroup label={intl.formatMessage({ id: "ARRIVAL" })} />
						</TableCell>

						<TableCell>
							<FormGroup label={undefined}>
								<Controller
									name={ptaName}
									control={control}
									render={({ field, formState }) => (
										<React.Fragment>
											<DatePicker {...field} disabled={!isRTR} showTimeInput />
											<FormErrorMessage name={field.name} errors={formState.errors} />
										</React.Fragment>
									)}
								/>
							</FormGroup>
						</TableCell>

						<TableCell>
							<FormGroup label={undefined}>
								<Controller
									name={etaName}
									control={control}
									render={({ field, formState }) => (
										<React.Fragment>
											<DatePicker {...field} showTimeInput />
											<FormErrorMessage name={field.name} errors={formState.errors} />
										</React.Fragment>
									)} />
							</FormGroup>
						</TableCell>

						<TableCell>
							<FormGroup overflow="hidden" label={undefined}>
								<Controller
									name={ataName}
									control={control}
									render={({ field, formState }) => (
										<React.Fragment>
											<DatePicker {...field} showTimeInput />
											<FormErrorMessage name={field.name} errors={formState.errors} />
										</React.Fragment>
									)} />
							</FormGroup>
						</TableCell>

						{hasNotificationTimes && (
							<TableCell>
								<FormGroup overflow="hidden" label={undefined}>
									<Controller
										name={atnName}
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<DatePicker {...field} showTimeInput />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								</FormGroup>
							</TableCell>
						)}
					</TableRow>

					<TableRow>
						<TableCell>
							<FormGroup label={intl.formatMessage({ id: "START_OPERATION" })} />
						</TableCell>

						<TableCell></TableCell>

						<TableCell>
							<FormGroup label={undefined} overflow="hidden">
								<Controller
									name={etsoName}
									control={control}
									render={({ field, formState }) => (
										<React.Fragment>
											<DatePicker {...field} showTimeInput />
											<FormErrorMessage name={field.name} errors={formState.errors} />
										</React.Fragment>
									)} />
							</FormGroup>
						</TableCell>

						<TableCell>
							<FormGroup label={undefined} overflow="hidden">
								<Controller
									name={atsoName}
									control={control}
									render={({ field, formState }) => (
										<React.Fragment>
											<DatePicker {...field} showTimeInput />
											<FormErrorMessage name={field.name} errors={formState.errors} />
										</React.Fragment>
									)} />
							</FormGroup>
						</TableCell>

						<TableCell></TableCell>
					</TableRow>

					<TableRow>
						<TableCell>
							<FormGroup label={intl.formatMessage({ id: "END_OPERATION" })} />
						</TableCell>

						<TableCell></TableCell>

						<TableCell>
							<FormGroup label={undefined} overflow="hidden">
								<Controller
									name={eteoName}
									control={control}
									render={({ field, formState }) => (
										<React.Fragment>
											<DatePicker {...field} showTimeInput />
											<FormErrorMessage name={field.name} errors={formState.errors} />
										</React.Fragment>
									)} />
							</FormGroup>
						</TableCell>

						<TableCell>
							<FormGroup label={undefined} overflow="hidden">
								<Controller
									name={ateoName}
									control={control}
									render={({ field, formState }) => (
										<React.Fragment>
											<DatePicker {...field} showTimeInput />
											<FormErrorMessage name={field.name} errors={formState.errors} />
										</React.Fragment>
									)} />
							</FormGroup>
						</TableCell>

						<TableCell></TableCell>
					</TableRow>

					<TableRow sx={{ "& td": { borderBottom: "none" } }}>
						<TableCell>
							<FormGroup label={intl.formatMessage({ id: "DEPARTURE" })} />
						</TableCell>

						<TableCell>
							<FormGroup label={undefined} overflow="hidden">
								<Controller
									name={ptdName}
									control={control}
									render={({ field, formState }) => (
										<React.Fragment>
											<DatePicker {...field} disabled={!isRTR} showTimeInput />
											<FormErrorMessage name={field.name} errors={formState.errors} />
										</React.Fragment>
									)} />
							</FormGroup>
						</TableCell>

						<TableCell>
							<FormGroup label={undefined} overflow="hidden">
								<Controller
									name={etdName}
									control={control}
									render={({ field, formState }) => (
										<React.Fragment>
											<DatePicker {...field} showTimeInput />
											<FormErrorMessage name={field.name} errors={formState.errors} />
										</React.Fragment>
									)} />
							</FormGroup>
						</TableCell>

						<TableCell>
							<FormGroup label={undefined} overflow="hidden">
								<Controller
									name={atdName}
									control={control}
									render={({ field, formState }) => (
										<React.Fragment>
											<DatePicker {...field} showTimeInput />
											<FormErrorMessage name={field.name} errors={formState.errors} />
										</React.Fragment>
									)} />
							</FormGroup>
						</TableCell>

						{hasNotificationTimes && (
							<TableCell>
								<FormGroup label={undefined} overflow="hidden">
									<Controller
										name={dtnName}
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<DatePicker {...field} showTimeInput />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								</FormGroup>
							</TableCell>
						)}
					</TableRow>
				</TableBody>
			</Table>
		</TableContainer>
	);
};

export default VoyageCallTimesFormFields;
